import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { getAnalysis, setActualGrade, startAnalysis } from "../lib/api";
import { gradeBand, readyVerdict, nextBoundary, performanceBand } from "../lib/scoreUtils";
import { AccuracyBadge } from "../components/examiner/AccuracyBadge";
import ExtractionSummary from "../components/examiner/ExtractionSummary";
import ConfirmGradePanel from "../components/examiner/ConfirmGradePanel";
import DraftHistory from "../components/examiner/DraftHistory";
import StructurePlannerReport from "../components/examiner/StructurePlannerReport";
import { ReflectiveDepthPanel } from "../components/examiner/ReflectiveDepthPanel";
import ImproveThisButton from "../components/examiner/ImproveThisButton";
import SubmissionIntelligencePanel from "../components/examiner/SubmissionIntelligencePanel";
import { fetchMe } from "../lib/api";
import { CRITERIA_HELP } from "../lib/criteriaHelp";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { MyAELogo } from "../components/myae/Logo";
import {
  Printer, ArrowLeft, Loader2, Check, X, Target, Rocket, Award, ThumbsUp,
  GraduationCap, BookOpen, Quote, FileText, TrendingDown, Gauge, HelpCircle, Brain,
} from "lucide-react";
import { toast } from "sonner";

const RISK_CAT = {
  web_common: {
    label: "Common academic expression",
    color: "#B45309",
    why: "This wording is very close to phrasing that appears widely in academic writing online — it can read as generic rather than showing your own thinking.",
    fix: "Rewrite this line in your own voice. Tie it to a specific example from your reading, or add a concrete detail only you would know.",
  },
  structure_similar: {
    label: "Sentence structure resembles sources",
    color: "#4F46E5",
    why: "The sentence structure closely mirrors phrasings often seen in published academic sources, which can weaken originality.",
    fix: "Rephrase in shorter, more direct sentences and add your own commentary or example after the point.",
  },
  needs_citation: {
    label: "This claim needs a citation",
    color: "#7C3AED",
    why: "This looks like a specific factual or scholarly claim, but no source has been credited for it.",
    fix: "Add a Harvard-style in-text citation to a peer-reviewed source that supports the claim, and include it in your reference list.",
  },
};

function highlightText(text, items) {
  if (!items || !items.length) return text;
  let nodes = [text];
  items.forEach((it, idx) => {
    const q = it.quote || "";
    if (!q) return;
    const next = [];
    nodes.forEach((node) => {
      if (typeof node !== "string") { next.push(node); return; }
      const i = node.toLowerCase().indexOf(q.toLowerCase());
      if (i === -1) { next.push(node); return; }
      next.push(node.slice(0, i));
      next.push(
        <mark key={`${idx}-${i}`} title={`${RISK_CAT[it.category]?.label || "Review"}: ${it.note || ""}`}
          style={{ backgroundColor: "#FEF3C7", borderBottom: `2px solid ${RISK_CAT[it.category]?.color || "#B45309"}`, padding: "0 1px" }}>
          {node.slice(i, i + q.length)}
        </mark>
      );
      next.push(node.slice(i + q.length));
    });
    nodes = next;
  });
  return nodes;
}

const studentId = (id = "") => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return "MyAE-2026-" + String(10000 + (h % 89999));
};

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return "—"; }
};

const Section = ({ icon: Icon, title, children, testId, className = "" }) => (
  <section className={`mt-8 ${className}`} data-testid={testId}>
    <div className="flex items-center gap-2 mb-4">
      {Icon && <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center"><Icon size={16} className="text-indigo-600" /></div>}
      <h2 className="font-display text-lg font-semibold text-zinc-900 tracking-tight">{title}</h2>
    </div>
    {children}
  </section>
);

const CriteriaHelpPopover = ({ label }) => {
  const help = CRITERIA_HELP[label];
  if (!help) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`How ${label} is graded`}
          data-testid={`criteria-help-${label.replace(/\s+/g, "-").toLowerCase()}`}
          className="no-print text-zinc-400 hover:text-indigo-600 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-200 rounded-full"
        >
          <HelpCircle size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="w-[340px] p-4 text-left"
        data-testid={`criteria-help-content-${label.replace(/\s+/g, "-").toLowerCase()}`}
      >
        <p className="font-display text-sm font-semibold text-zinc-900 mb-1">{help.title}</p>
        <p className="text-xs text-zinc-600 leading-relaxed mb-3">{help.what}</p>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 mb-1.5">Grade bands</p>
        <ul className="space-y-1.5 mb-3">
          {help.bands.map((b, i) => (
            <li key={i} className="text-[12px] leading-relaxed text-zinc-700">
              <b className="text-zinc-900">{b.label}.</b> {b.detail}
            </li>
          ))}
        </ul>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">To move up a band</p>
        <p className="text-[12px] leading-relaxed text-indigo-700">{help.next}</p>
      </PopoverContent>
    </Popover>
  );
};

const CriteriaBar = ({ label, value, note, invert }) => {
  const raw = Number(value) || 0;
  // For inverted metrics (lower is better), mirror to keep band semantics consistent.
  const effective = invert ? Math.max(0, 100 - raw) : raw;
  const band = performanceBand(effective);
  const hoverTitle = `${band.label} — ${raw}%${invert ? " (lower is better)" : ""}`;
  return (
    <div className="py-3 border-b border-zinc-100 last:border-0" data-testid={`criteria-row-${band.key}`}>
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <span className="text-sm font-medium text-zinc-800 flex items-center gap-1.5">
          {label}
          <CriteriaHelpPopover label={label} />
        </span>
        <span
          title={hoverTitle}
          data-testid="criteria-band-badge"
          data-band={band.key}
          data-percentage={raw}
          className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-tight cursor-help select-none"
          style={{ color: band.color, backgroundColor: band.bg, border: `1px solid ${band.border}` }}
        >
          {band.label}
        </span>
      </div>
      <div
        className="h-1.5 rounded-full bg-zinc-100 overflow-hidden"
        title={hoverTitle}
      >
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${effective}%`, backgroundColor: band.bar }}
        />
      </div>
      {note && <p className="text-xs text-zinc-500 leading-relaxed mt-1.5">{note}</p>}
    </div>
  );
};

export default function ExaminerReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);
  const [calib, setCalib] = useState(null);
  const [showGradeInput, setShowGradeInput] = useState(false);
  const [markInput, setMarkInput] = useState("");
  const [savingMark, setSavingMark] = useState(false);
  const [structureData, setStructureData] = useState(null);
  const [isUltraPro, setIsUltraPro] = useState(false);

  useEffect(() => {
    fetchMe().then((u) => setIsUltraPro(!!u?.is_ultra_pro)).catch(() => {});
  }, []);

  useEffect(() => {
    getAnalysis(id).then((doc) => {
      setA(doc);
      setStructureData(doc?.report?.suggested_structure || null);
      if (doc?.graded_by_university) setCalib({ actual: doc.actual_grade, difference: doc.grade_difference });
    }).catch(() => setErr(true));
  }, [id]);

  const saveMark = async () => {
    const val = parseFloat(markInput);
    if (isNaN(val) || val < 0 || val > 100) { toast.error("Enter a mark between 0 and 100."); return; }
    setSavingMark(true);
    try {
      const res = await setActualGrade(id, val);
      setCalib({ actual: res.actual, difference: res.difference });
      setShowGradeInput(false);
      toast.success("Thanks — your actual mark helps improve accuracy.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your mark.");
    } finally {
      setSavingMark(false);
    }
  };

  if (err) return <div className="min-h-screen flex items-center justify-center text-zinc-500 bg-zinc-50">Report not found.</div>;
  if (!a || a.status !== "complete") return (
    <div className="min-h-screen flex items-center justify-center gap-3 text-zinc-500 bg-zinc-50">
      <Loader2 className="animate-spin" /> Loading report…
    </div>
  );

  const r = a.report || {};
  const rd = r.readiness || {};
  const g = r.estimated_grade || {};
  const ct = r.critical_thinking || {};
  const ref = r.referencing || {};
  const sim = r.similarity || {};
  const ai = r.ai_risk || {};
  const stc = r.structure || {};
  const path = r.path_to_target || {};
  const ready = rd.submission_ready ?? 0;
  const verdict = readyVerdict(ready);
  const conf = r.prediction_confidence ?? 75;
  const boundary = nextBoundary(g.percentage || 0);
  const potential = r.potential_grade || {};
  const gradeRange = r.grade_range || {};
  const briefWeighting = r.brief_weighting || {};
  const priorities = r.priority_improvements || [];
  const strengths = r.strengths || [];
  const outcomes = r.learning_outcomes || a.outcomes || "";
  const overall = r.examiner_report || r.lecturer_feedback || "";

  const _linkNote = (ref.matched_in_text_citations != null && (ref.unique_in_text_citations || 0) > 0 && ref.total_references > 0)
    ? ` ${ref.matched_in_text_citations} of ${ref.unique_in_text_citations} distinct citation${ref.unique_in_text_citations === 1 ? "" : "s"} matched to the reference list`
      + ((ref.unused_references || 0) > 0 ? `; ${ref.unused_references} reference${ref.unused_references === 1 ? "" : "s"} never cited` : "")
      + "."
    : "";
  const refNote = (ref.total_references > 0)
    ? `${ref.total_references} reference${ref.total_references === 1 ? "" : "s"} detected`
      + (ref.detected_style && ref.detected_style !== "Unknown" ? ` (${ref.detected_style} style)` : "")
      + (ref.in_text_citations ? `, ${ref.in_text_citations} in-text citation${ref.in_text_citations === 1 ? "" : "s"}` : "")
      + (_linkNote || (ref.issues && ref.issues.length ? `. ${ref.issues[0].detail}` : ". No formatting issues detected."))
    : ref.reference_section_found
      ? `Reference section detected. Formatting could not be fully analysed. ${ref.references_detected ?? 0} potential references identified.`
      : (ref.issues?.[0]?.detail || "No reference list detected.");
  // Displayed score. Only clamp to 30 when the parser is CONFIDENT no citations exist
  // (or when we're rendering a pre-fix analysis where citation_status is undefined and
  // total_references is 0). If detection was INCONCLUSIVE — the parser found academic
  // signal but could not confirm citations, OR the doc is image-heavy so references
  // can't be read — trust the deterministic formatting_score so the student isn't
  // penalised twice for the same uncertainty.
  const _confidentNone = !ref.citation_status
    ? (ref.total_references === 0 && (ref.in_text_citations || 0) === 0)  // legacy analyses
    : (ref.citation_status === "confident_none" && ref.references_status !== "inconclusive_image");
  const refScore = _confidentNone ? Math.min(rd.referencing ?? 0, 30) : (rd.referencing ?? 0);

  const scores = [
    ["Assignment Fit", r.assignment_analysis?.answered_question?.score ?? 0, r.assignment_analysis?.answered_question?.comment, false],
    ["Critical Analysis", rd.critical_thinking ?? 0, ct.summary, false],
    ["Academic Writing", rd.academic_writing ?? 0, "Composite of grammar, tone, vocabulary, sentence variety, flow, objectivity and readability.", false],
    ["Referencing", refScore, refNote, false],
    ["Writing Authenticity", rd.writing_authenticity ?? 0, ai.summary || "Your writing reads as personal and specific throughout — no passages flagged for review.", false],
    ["Structure", rd.structure ?? 0, stc.consistency?.comment, false],
  ];

  const simSources = sim.sources || [];
  const docsUsed = r.documents_used && r.documents_used.length ? r.documents_used : [
    { label: "Assignment Brief", used: r.analysis_inputs?.brief },
    { label: "Student Submission", used: r.analysis_inputs?.submission },
    { label: "Marking Rubric", used: r.analysis_inputs?.rubric },
    { label: "Learning Outcomes", used: r.analysis_inputs?.outcomes },
  ];

  return (
    <div className="min-h-screen bg-zinc-100 py-8 print:py-0 print:bg-white">
      {/* Toolbar */}
      <div className="no-print max-w-[860px] mx-auto mb-5 flex items-center justify-between px-4">
        <button onClick={() => navigate("/dashboard")} data-testid="report-back-btn"
          className="inline-flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors">
          <ArrowLeft size={16} /> Back to dashboard
        </button>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/community")} data-testid="community-link"
            className="inline-flex items-center gap-2 rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:border-indigo-500 hover:text-indigo-600 transition-colors">
            <Award size={15} /> Community
          </button>
          <button onClick={() => window.print()} data-testid="print-report-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-medium shadow-sm hover:bg-indigo-700 transition-all active:scale-[0.98]">
            <Printer size={16} /> Download PDF
          </button>
        </div>
      </div>

      {/* Paper */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
        className="max-w-[860px] mx-auto bg-white shadow-[0_12px_40px_rgba(0,0,0,0.08)] print:shadow-none rounded-2xl print:rounded-none overflow-hidden"
        data-testid="examiner-report-paper">
        <div className="px-8 sm:px-14 py-10 sm:py-12 print:px-10 print:py-8">
          {/* Letterhead */}
          <div className="flex items-start justify-between gap-4 flex-wrap border-b border-zinc-200 pb-6">
            <div>
              <MyAELogo size={30} />
              <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900 mt-4">Examiner Report</h1>
            </div>
            <div className="text-right text-xs text-zinc-500 font-mono leading-relaxed">
              <p>Student ID: <span className="text-zinc-900">{studentId(a.id)}</span></p>
              <p>Date examined: <span className="text-zinc-900">{fmtDate(a.completed_at || a.created_at)}</span></p>
              <p>Ref: <span className="text-zinc-900">{a.id.slice(0, 8).toUpperCase()}</span></p>
            </div>
          </div>

          {/* Assignment */}
          <div className="mt-6">
            <p className="overline text-zinc-400">Assignment</p>
            <p className="font-display text-xl font-medium text-zinc-900 mt-1 leading-snug">
              {a.title || a.question || a.filename || "Untitled submission"}
            </p>
            {a.question && a.title && <p className="text-sm text-zinc-500 mt-1 leading-relaxed">{a.question}</p>}
          </div>

          {/* Document Extraction Summary — shown at the very top so students know
              exactly what MyAE detected before they read the analysis. */}
          <ExtractionSummary summary={r.extraction_summary} filename={a.filename} analysisId={a.id} />

          {/* Executive Summary */}
          <Section icon={GraduationCap} title="Executive Summary" testId="report-executive-summary" className="mt-7">
            {a.submission_scope === "contribution" && !r.grade_locked && (
              <div className="rounded-2xl border-l-4 border-indigo-500 bg-indigo-50/60 p-4 mb-4" data-testid="report-contribution-banner">
                <p className="text-sm text-zinc-800 leading-relaxed">
                  <b className="text-indigo-700">Individual contribution mode.</b> This evaluation relates only to the uploaded contribution and should not be interpreted as a prediction of the overall group assignment grade.
                </p>
              </div>
            )}
            {r.grade_locked ? (
              <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 py-8 px-8 text-center" data-testid="report-grade-locked">
                <p className="overline text-amber-600">Predicted grade unavailable</p>
                <p className="font-display text-2xl font-medium text-zinc-900 mt-2">Assignment brief required</p>
                <p className="text-sm text-amber-800 mt-3 leading-relaxed max-w-xl mx-auto">{r.grade_locked_message}</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-5 gap-4">
                <div className="md:col-span-2 rounded-2xl bg-gradient-to-br from-indigo-600 to-indigo-800 text-white p-6 flex flex-col justify-center shadow-[0_10px_30px_rgba(79,70,229,0.35)]" data-testid="report-grade-hero">
                  <p className="overline text-indigo-200">{a.submission_scope === "contribution" ? "Predicted grade for your contribution" : "Predicted grade range"}</p>
                  {gradeRange && gradeRange.most_likely != null ? (
                    <>
                      <p className="font-display font-semibold tabular-nums leading-none mt-1" style={{ fontSize: "3.25rem" }} data-testid="grade-range">
                        {gradeRange.low}<span className="text-3xl mx-1 align-middle text-indigo-200">–</span>{gradeRange.high}<span className="text-3xl align-top">%</span>
                      </p>
                      <p className="text-sm text-indigo-100 mt-1" data-testid="grade-most-likely">
                        Most likely: <b className="text-white font-display text-lg tabular-nums">{gradeRange.most_likely}%</b>
                      </p>
                    </>
                  ) : (
                    <p className="font-display font-semibold tabular-nums leading-none mt-1" style={{ fontSize: "4rem" }}>
                      {g.percentage ?? "—"}<span className="text-3xl align-top">%</span>
                    </p>
                  )}
                  <p className="font-display text-xl font-medium mt-1">{g.classification || gradeBand(g.percentage || 0)}</p>
                  <p className="text-xs text-indigo-200 mt-1">{boundary.text}</p>
                  {a.module_level_display && (
                    <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="grade-level-pill">
                      <span className="text-[11px] text-indigo-100/90 inline-flex items-center gap-1 bg-white/10 rounded-full px-2 py-0.5">
                        Graded using the <b>{a.module_level_display}</b> assessment rubric
                      </span>
                      <button
                        onClick={async () => {
                          const map = {'3':'Foundation (Level 3)','4':'Year 1 (Level 4)','5':'Year 2 (Level 5)','6':'Final Year (Level 6)','7':'Postgraduate (Level 7)'};
                          const opts = Object.entries(map).map(([k,v]) => `${k}. ${v}`).join('\n');
                          const pick = window.prompt(`Change academic level & re-analyse?\n\n${opts}\n\nEnter 3, 4, 5, 6 or 7:`, a.module_level || '5');
                          if (!pick || !['3','4','5','6','7'].includes(pick.trim())) return;
                          if (pick.trim() === (a.module_level || '').trim()) { toast.message('That is already the current level.'); return; }
                          try {
                            const res = await startAnalysis({
                              text: a.text, filename: a.filename,
                              question: a.question, outcomes: a.outcomes, rubric: a.rubric,
                              parent_id: a.id, module_level: pick.trim(),
                              university: a.university, subject: a.subject, module: a.module,
                              faculty: a.faculty, department: a.department,
                              assessment_type: a.assessment_type,
                            });
                            toast.success(`Re-analysing at ${map[pick.trim()]}…`);
                            navigate(`/report/${res.id}`);
                          } catch (e) {
                            if (e?.response?.status === 402) {
                              toast.error("Re-analysing uses one credit and needs Pro or credits.");
                              navigate("/pricing");
                            } else {
                              toast.error(e?.response?.data?.detail || "Could not re-analyse.");
                            }
                          }
                        }}
                        data-testid="change-level-btn"
                        className="text-[11px] text-indigo-100/90 hover:text-white underline underline-offset-2"
                      >
                        Change level ↻
                      </button>
                    </div>
                  )}
                  {(potential.low || potential.high) && (
                    <p className="text-xs text-emerald-200 mt-2">Potential after changes: {potential.low}{potential.high && potential.high !== potential.low ? `–${potential.high}` : ""}%</p>
                  )}
                </div>
                <div className="md:col-span-3 grid sm:grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-zinc-200 p-5" data-testid="report-confidence">
                    <div className="flex items-center gap-1.5 text-zinc-400 mb-1"><Gauge size={14} /><p className="overline">Confidence</p></div>
                    <p className="font-display text-2xl font-semibold text-zinc-900">{conf}%<span className="text-sm font-normal text-zinc-500 ml-1">{r.confidence_level || (conf >= 80 ? "High" : conf >= 60 ? "Medium" : "Low")}</span></p>
                    <div className="h-1.5 rounded-full bg-zinc-100 mt-2 overflow-hidden"><div className="h-full rounded-full bg-indigo-500" style={{ width: `${conf}%` }} /></div>
                    <div className="mt-3 space-y-1">
                      {(r.confidence_factors || []).map((f, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          {f.ok ? <Check size={12} className="text-emerald-600 shrink-0" /> : <X size={12} className="text-red-500 shrink-0" />}
                          <span className={f.ok ? "text-zinc-700" : "text-zinc-400"}>{f.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-zinc-200 p-5 flex flex-col items-center justify-center text-center">
                    <p className="overline text-zinc-400">Ready to submit?</p>
                    <p className="font-display text-2xl font-semibold mt-1" style={{ color: verdict.color }}>{verdict.label}</p>
                    <p className="text-xs text-zinc-500 mt-0.5 font-mono">{ready}% readiness</p>
                  </div>
                </div>
              </div>
            )}
            <div className="no-print"><AccuracyBadge variant="report" /></div>
          </Section>

          {/* Draft resubmission — version history, improvement callout, upload new draft ↻ */}
          <DraftHistory analysis={a} />

          {/* Referenced Documents */}
          <Section icon={FileText} title="Referenced Documents" testId="report-documents-analysed">
            <div className="rounded-2xl border border-zinc-200 p-5">
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
                {docsUsed.map((d, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    {d.used ? <Check size={15} className="text-emerald-600 shrink-0" /> : <X size={15} className="text-red-400 shrink-0" />}
                    <span className={d.used ? "text-zinc-800" : "text-zinc-400"}>{d.label}{d.used ? "" : " (not provided)"}</span>
                  </div>
                ))}
              </div>
              {r.assessment_type && (
                <p className="text-xs text-zinc-500 mt-3 pt-3 border-t border-zinc-100">Assessment type detected: <b className="text-zinc-700">{r.assessment_type}</b></p>
              )}
            </div>
          </Section>

          {/* Brief Weighting — what MyAE weighted based on the command verbs in the brief */}
          {briefWeighting.applied && (briefWeighting.verbs || []).length > 0 && (
            <Section icon={Target} title="How this brief was weighted" testId="report-brief-weighting">
              <div className="rounded-2xl border border-indigo-100 bg-indigo-50/40 p-5">
                <p className="text-sm text-zinc-800 leading-relaxed">
                  MyAE analysed the command verbs in the brief and calibrated the marking emphasis to match. Detected verbs:{" "}
                  {(briefWeighting.verbs || []).map((v, i) => (
                    <span key={i} className="inline-block bg-white border border-indigo-200 rounded-full px-2 py-0.5 text-xs font-medium text-indigo-700 mr-1.5 mb-1" data-testid={`weighting-verb-${v.verb.replace(/\s+/g, "-")}`}>
                      {v.verb}
                    </span>
                  ))}
                </p>
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  {Object.entries(briefWeighting.profile?.weights || {}).sort((a,b) => b[1] - a[1]).map(([k, w]) => {
                    const labels = {
                      knowledge_and_understanding: "Knowledge & Understanding",
                      structure: "Structure",
                      critical_analysis: "Critical Analysis",
                      evidence: "Evidence Quality",
                      referencing: "Referencing",
                    };
                    const delta = (briefWeighting.profile?.delta || {})[k] || 0;
                    return (
                      <div key={k} className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-zinc-700 font-medium">{labels[k] || k}</span>
                            <span className="font-mono text-zinc-900">{w.toFixed(0)}%
                              {delta !== 0 && (
                                <span className={`ml-1.5 ${delta > 0 ? "text-emerald-600" : "text-amber-600"}`}>
                                  {delta > 0 ? "▲" : "▼"}{Math.abs(delta).toFixed(0)}
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="h-1.5 rounded-full bg-white overflow-hidden border border-indigo-100">
                            <div className="h-full rounded-full bg-indigo-500" style={{ width: `${Math.min(100, w * 2)}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-zinc-500 mt-3">
                  Arrows show the shift vs. the default rubric. MyAE also ensures a single weakness only impacts your grade once — no double-penalisation across sections.
                </p>
              </div>
            </Section>
          )}

          {/* Suggested Structure (word-count targets vs actual) */}
          {!r.grade_locked && (
            <Section icon={FileText} title="Suggested Structure" testId="report-suggested-structure">
              <StructurePlannerReport
                analysisId={a.id}
                structure={structureData}
                onComputed={setStructureData}
              />
            </Section>
          )}

          {/* Strengths */}
          {strengths.length > 0 && (
            <Section icon={ThumbsUp} title="Strengths" testId="report-strengths">
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5 space-y-2.5">
                {strengths.map((s, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-sm leading-relaxed text-zinc-800">
                    <Check size={16} className="text-emerald-600 mt-0.5 shrink-0" /><span>{s}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Areas for Improvement */}
          <Section icon={Rocket} title="Areas for Improvement" testId="report-areas-improvement">
            {priorities.length > 0 ? (
              <div className="rounded-2xl border border-zinc-200 divide-y divide-zinc-100 overflow-hidden">
                {priorities.slice(0, 5).map((p, i) => (
                  <div key={i} className="flex items-start gap-3 px-5 py-3.5 text-sm leading-relaxed">
                    <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 font-medium text-xs flex items-center justify-center shrink-0">{i + 1}</span>
                    <span className="flex-1 text-zinc-800">{p.action}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <ImproveThisButton
                        analysisId={a.id}
                        issue={p}
                        isUltraPro={isUltraPro}
                      />
                      {typeof p.impact === "number" && p.impact > 0 && (
                        <span className="font-mono text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">+{p.impact}%</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <ol className="rounded-2xl border border-zinc-200 divide-y divide-zinc-100 overflow-hidden">
                {(r.improvement_suggestions || []).slice(0, 3).map((p, i) => (
                  <li key={i} className="flex gap-3 px-5 py-3.5 text-sm leading-relaxed text-zinc-800">
                    <span className="font-mono font-semibold text-indigo-600">{i + 1}.</span><span>{p}</span>
                  </li>
                ))}
              </ol>
            )}
            <p className="text-xs text-zinc-400 mt-2">Impact figures are estimates — actual marking depends on many factors.</p>
          </Section>

          {/* Assessment Criteria */}
          <Section icon={Target} title="Assessment Criteria" testId="report-criteria">
            <div className="rounded-2xl border border-zinc-200 px-5 py-1">
              {scores.map(([l, v, n, inv]) => <CriteriaBar key={l} label={l} value={v} note={n} invert={inv} />)}
            </div>
          </Section>

          {/* Reflective-practice depth-of-use — auditable, deterministic */}
          {r.reflective_assessment && (r.reflective_assessment.frameworks_detected || 0) > 0 && (
            <Section icon={Brain} title="Reflective-practice depth" testId="report-reflective-depth">
              <ReflectiveDepthPanel assessment={r.reflective_assessment} spans={r.evidence_spans || []} />
            </Section>
          )}

          {/* Learning Outcomes */}
          {outcomes && (
            <Section icon={BookOpen} title="Learning Outcomes" testId="report-outcomes">
              <div className="rounded-2xl border border-zinc-200 p-5 text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap">{outcomes}</div>
            </Section>
          )}

          {/* Similarity Analysis */}
          <Section icon={FileText} title="Similarity Analysis" testId="report-similarity">
            <p className="text-sm text-zinc-500 mb-3">Compared against:</p>
            <div className="rounded-2xl border border-zinc-200 divide-y divide-zinc-100 overflow-hidden">
              {simSources.map((s) => (
                <div key={s.key} className="flex items-start gap-3 px-5 py-3.5">
                  {s.checked ? <Check size={16} className="text-emerald-600 mt-0.5 shrink-0" /> : <span className="mt-0.5 shrink-0 w-4 h-4 rounded border border-zinc-300 inline-block" />}
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-zinc-800">{s.label}{s.coming_soon && !s.checked ? " (coming soon)" : ""}</span>
                      {s.checked && (
                        <span className="font-mono text-xs font-semibold" style={{ color: (s.percentage || 0) >= 15 ? "#D97706" : "#059669" }}>{s.percentage ?? 0}%</span>
                      )}
                    </div>
                    {s.key === "previous_uploads" && s.checked && (
                      <p className="text-xs text-zinc-500 mt-0.5">{s.documents_compared > 0 ? `Compared against ${s.documents_compared} of your own document${s.documents_compared === 1 ? "" : "s"}.` : "No previous uploads to compare yet."}</p>
                    )}
                    {s.key === "corpus" && s.checked && (
                      <p className="text-xs text-zinc-500 mt-0.5">
                        {(s.matches_count || 0) > 0
                          ? `${s.percentage}% overlap with ${s.matches_count} of ${s.documents_compared} anonymised corpus document${s.documents_compared === 1 ? "" : "s"}.`
                          : `No significant overlap across ${s.documents_compared} anonymised corpus document${s.documents_compared === 1 ? "" : "s"}.`}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-400 mt-2">Similarity Analysis is not plagiarism detection and is not affiliated with, or compatible with, Turnitin.</p>
          </Section>

          {/* Similarity Risk Scan */}
          {(r.risk_scan?.items || []).length > 0 && (
            <Section icon={TrendingDown} title="Similarity Risk Scan" testId="report-risk-scan">
              {r.risk_scan.summary && <p className="text-sm text-zinc-600 mb-1">{r.risk_scan.summary}</p>}
              <p className="text-xs text-zinc-400 mb-3">Guidance only — flagged phrases are marked <b>&ldquo;Review recommended&rdquo;</b>, not plagiarism findings.</p>
              <div className="rounded-2xl border border-zinc-200 divide-y divide-zinc-100 overflow-hidden">
                {r.risk_scan.items.map((it, i) => {
                  const cat = RISK_CAT[it.category] || {};
                  return (
                    <div key={i} className="px-5 py-3.5" data-testid="risk-item">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full" style={{ color: cat.color, backgroundColor: "#F4F4F5" }}>{cat.label || "Review"}</span>
                        <span className="text-[11px] text-amber-600 font-semibold">Review recommended</span>
                      </div>
                      <p className="text-sm text-zinc-800 italic">&ldquo;{it.quote}&rdquo;</p>
                      {it.note && <p className="text-xs text-zinc-500 mt-1">{it.note}</p>}
                      {(cat.why || cat.fix) && (
                        <div className="mt-2 pt-2 border-t border-zinc-100 grid sm:grid-cols-2 gap-3">
                          {cat.why && (
                            <div>
                              <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-0.5">Why it was flagged</p>
                              <p className="text-xs text-zinc-700 leading-relaxed">{cat.why}</p>
                            </div>
                          )}
                          {cat.fix && (
                            <div>
                              <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 mb-0.5">How to improve it</p>
                              <p className="text-xs text-zinc-700 leading-relaxed">{cat.fix}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="overline text-zinc-400 mt-6 mb-2">Your submission — flagged phrases highlighted</p>
              <div className="rounded-2xl border border-zinc-200 p-5 space-y-3 max-h-[420px] overflow-auto" data-testid="risk-highlighted-text">
                {(r.paragraphs || []).map((p, pi) => (<p key={pi} className="text-sm leading-relaxed text-zinc-700">{highlightText(p, r.risk_scan.items)}</p>))}
              </div>
            </Section>
          )}

          {/* Writing Style & Authenticity Review */}
          {(ai.flagged_paragraphs || []).length > 0 && (
            <Section icon={Quote} title="Writing Style & Authenticity Review" testId="report-authenticity-review">
              <p className="text-sm text-zinc-700 leading-relaxed mb-4">
                {ai.summary || "A few passages read as common academic expression or contain broad unsupported claims — personalise the wording, tighten the analysis, and add specific evidence for the key points."}
              </p>
              <div className="rounded-2xl border border-zinc-200 divide-y divide-zinc-100 overflow-hidden" data-testid="authenticity-passages">
                {ai.flagged_paragraphs.map((f, i) => (
                  <div key={i} className="px-5 py-3.5" data-testid={`authenticity-passage-${i}`}>
                    <div className="flex flex-wrap items-center gap-1.5 mb-2">
                      <span className="text-[11px] text-amber-600 font-semibold">Review recommended</span>
                      {(f.reasons || []).map((rr, ri) => (
                        <span key={ri} className="text-[11px] text-amber-900 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full">{rr}</span>
                      ))}
                    </div>
                    {f.quote && <p className="text-sm text-zinc-800 italic leading-relaxed">&ldquo;{f.quote}&rdquo;</p>}
                    {f.suggestion && (
                      <div className="mt-2 pt-2 border-t border-zinc-100">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 mb-0.5">How to improve it</p>
                        <p className="text-[13px] text-zinc-700 leading-relaxed">{f.suggestion}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Overall Feedback */}
          {(overall || r.if_lecturer || r.what_a_first_looks_like) && (
            <Section icon={Quote} title="Overall Feedback" testId="report-overall-feedback">
              {overall && <p className="text-[15px] leading-[1.75] text-zinc-800">{overall}</p>}
              {r.if_lecturer && (
                <div className="mt-4 rounded-2xl bg-indigo-50 border-l-4 border-indigo-500 px-5 py-4" data-testid="report-if-lecturer">
                  <p className="overline text-indigo-500 mb-2">If I were your lecturer…</p>
                  <p className="text-[15px] italic leading-relaxed text-zinc-800">{r.if_lecturer}</p>
                </div>
              )}
              {r.what_a_first_looks_like && (
                <div className="mt-4 rounded-2xl border border-zinc-200 px-5 py-4" data-testid="report-what-a-first">
                  <p className="overline text-zinc-400 mb-2 flex items-center gap-1.5"><Award size={13} className="text-indigo-500" /> What would a First look like?</p>
                  <p className="text-[15px] italic leading-relaxed text-zinc-800">{r.what_a_first_looks_like}</p>
                </div>
              )}
            </Section>
          )}

          {/* Next Steps */}
          {(path.steps || []).length > 0 && (
            <Section icon={Target} title="Next Steps" testId="report-path-to-target">
              <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-5">
                <div className="flex items-center gap-6 mb-4">
                  <span className="font-mono text-sm text-zinc-600">Now <b className="text-lg text-zinc-900">{g.percentage ?? 0}%</b></span>
                  <span className="text-indigo-400">→</span>
                  <span className="font-mono text-sm text-zinc-600">Target <b className="text-lg text-emerald-600">{path.target ?? "—"}%</b></span>
                </div>
                <div className="space-y-2.5 border-t border-indigo-100 pt-4">
                  {path.steps.map((s, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-sm leading-relaxed text-zinc-800">
                      <Check size={15} className="text-indigo-600 mt-0.5 shrink-0" /><span>{s}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Section>
          )}

          {/* Grade Confirmation — student-facing anonymised calibration loop */}
          {!r.grade_locked && (
            <ConfirmGradePanel
              analysisId={a.id}
              myaePrediction={g.percentage}
              alreadyConfirmed={!!a.grade_confirmed}
              existingGrade={a.official_grade}
            />
          )}

          {/* Submission Intelligence — one button: web similarity + evidence + risk */}
          <div className="mt-8 no-print" data-testid="si-panel-wrapper">
            <SubmissionIntelligencePanel analysisId={a.id} />
          </div>

          {/* Footer */}
          <div className="mt-10 pt-5 border-t border-zinc-200 flex items-center justify-between gap-4 flex-wrap text-[10px] text-zinc-400 font-mono uppercase tracking-wider">
            <span>MyAE · Indicative report — not an official university grade</span>
            <span>{studentId(a.id)}</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
